/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

// react and redux
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext, useParams, useLocation } from "react-router-dom";
// redux
import { useSelector, useDispatch } from "react-redux";
import { selectUser } from "../../../store/userSlice";
import authRoles from "../../../auth/authRoles";
// actions
import { getListing, addInstance, updateInstance, deleteInstance } from "../../../actions";
// forms
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
// BASE Components
import _ from "../../../../@lodash";
import BASELoading from "../../../../@BASE/core/BASELoading";
// material-ui components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
// custom components
import FormSectionHeader from "../../../shared-components/crud/instance/FormSectionHeader";
import SectionStatusSteps from "./SectionStatusSteps";
import NavigationArray from "./navigation/NavigationArray";
import DeleteModal from "../../../shared-components/crud/DeleteModal";

// form validation
const schema = yup.object().shape({
  lesson: yup.string().required("You must select a lesson"),
  title: yup.string().required("You must enter a name"),
  label: yup.string().required("You must enter a label"),
  classification: yup.string().required("You must select a section type"),
});

const SectionForm = (props) => {
  const { instance, handleInstanceAction, loading, setOnSave, setSaveDisabled } = useOutletContext();
  const [lessons, setLessons] = useState(null);
  const [inputDir, setInputDir] = useState('ltr');
  const user = useSelector(selectUser);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const routeParams = useParams();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const lesson = instance?.lesson?.id ?? queryParams.get('p');
  // form methods
  const { control, watch, reset, handleSubmit, formState, getValues, setValue } = useForm(
    {
      mode: "onChange",
      resolver: yupResolver(schema),
    }
  );
  // form state
  const { isValid, dirtyFields, errors } = formState;
  // watch form
  const form = watch();
  // get parent listing
  const handleGetLessons = async () => {
    try {
      const response = await dispatch(getListing('lessons')()).unwrap();
      setLessons(response);
      setValue('lesson', lesson);
    } catch (err) {
      console.log(err.message);
    }
  }
  // ensure projects are only fetched once
  useEffect(() => {
    // get users
    handleGetLessons();
  }, [dispatch, routeParams]);
  // form submission
  function onSubmit(data) {
    let dataToSend = {
        classification: data["classification"],
        label: data["label"],
        lesson: data["lesson"],
        order: data["order"],
        progress: data["progress"],
        status: data["status"],
        title: data["title"],
        user: data["user"]
    }
    // only add navigation if it exists
    if(data["navigation"].length > 0) {
        dataToSend["navigation"] = dataToSend["navigation"]
    }
    // dispatch add or update section
    if (routeParams.id === 'new') {
      handleInstanceAction(addInstance('sections')(data), 'post');
    } else {
      dataToSend["id"] = data["id"]
      handleInstanceAction(updateInstance('sections')(data), 'patch');
    }
  }
  // Pass the handleSubmit function to setOnSave
  useEffect(() => {
    setOnSave(() => handleSubmit(onSubmit));
  }, []);
  // Update the parent component with the form's validity and dirty state
  useEffect(() => {
    setSaveDisabled(_.isEmpty(dirtyFields) || !isValid);
  }, [formState]);
  // handle removal of lesson
  const handleRemoveSection = () => {
    // build request data
    const data = {
        id: instance.id,
        data: {
          user: user._id || user.id
        }
      };
    handleInstanceAction(deleteInstance('sections')(data), 'delete');
    // navigate to projects listing
    navigate('/lessons/' + lesson);
  }
  // on instance change, reset the form
  useEffect(() => {
    let data = {
      ...instance,
      lesson: lesson
    }
    reset({ ...data });
    setInputDir(instance.lesson?.course?.language?.rtl ? 'rtl' : 'ltr')
  }, [instance, reset]);
  // if data is missing, return loading
  if (_.isEmpty(form) || !instance || !lessons) {
    return <BASELoading />;
  }

  return (
    <div className="relative flex flex-col flex-auto">
      <div className="relative my-64 px-24 sm:px-32 max-w-4xl">
        <FormSectionHeader title="Basic Information" description="Enter the basic section details below." />
        <div className="mt-32 grid w-full gap-24 sm:grid-cols-4">
          <div className="sm:col-span-4">
            <Controller
              control={control}
              name="title"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Title"
                  placeholder="Title"
                  id="title"
                  error={!!errors.title}
                  helperText={errors?.title?.message}
                  variant="outlined"
                  required
                  fullWidth
                  size="small"
                />
              )}
            />
          </div>
          <div className="sm:col-span-2">
            <Controller
              control={control}
              name="label"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Label"
                  placeholder="Label"
                  id="label"
                  error={!!errors.label}
                  helperText={errors?.label?.message}
                  variant="outlined"
                  required
                  fullWidth
                  size="small"
                />
              )}
            />
          </div>
          <div className="sm:col-span-2">
            <FormControl
              variant="outlined"
              fullWidth
              error={!!errors.classification}
              required
            >
              <InputLabel id="classification">Type</InputLabel>
              <Controller
                control={control}
                name="classification"
                defaultValue=''
                render={({ field: { onChange, value } }) => (
                  <Select
                    value={value}
                    onChange={onChange}
                    label="Type"
                    placeholder="Type"
                    id="classification"
                    variant="outlined"
                    required
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="" disabled>Select a section type</MenuItem>
                    <MenuItem value="normal">Normal</MenuItem>
                    <MenuItem value="single">Single</MenuItem>
                  </Select>
                )}
              />
            </FormControl>
          </div>
        </div>

        <Divider className="my-40" />

        <FormSectionHeader title="Section Details" description="Enter the section details below." />
        <div className="mt-32 grid w-full gap-24 sm:grid-cols-4">
          <div className="sm:col-span-2">
            <FormControl
              variant="outlined"
              fullWidth
              error={!!errors.lesson}
              required
              disabled
            >
              <InputLabel id="project">Lesson</InputLabel>
              <Controller
                control={control}
                name="lesson"
                defaultValue=""
                render={({ field: { onChange, value } }) => (
                  <Select
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                    }}
                    label="Lessons"
                    placeholder="Lessons"
                    id="lesson"
                    variant="outlined"
                    required
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="" disabled>Select a Lesson</MenuItem>
                    {lessons.map((lesson) => (
                      <MenuItem value={lesson.id} key={lesson.id} className="space-x-12">
                        <span>{lesson.title}</span>
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </div>
          <div className="sm:col-span-2">
            <Controller
              control={control}
              name="order"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Order"
                  placeholder="Order"
                  id="order"
                  error={!!errors?.order}
                  helperText={errors?.order?.message}
                  variant="outlined"
                  type="number"
                  required
                  fullWidth
                  size="small"
                />
              )}
            />
          </div>
        </div>

        <Divider className="my-40" />

        <FormSectionHeader title="Section Status" description="These values are here for progress and status." />
        <div className="mt-32 grid w-full gap-24 sm:grid-cols-4">
          <div className="sm:col-span-4">
            <Typography variant='body2' className='font-semibold'>Progress:</Typography>
          </div>
          <div className="sm:col-span-2">
            <Controller
              control={control}
              name="progress.development"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Storyboard Progress"
                  placeholder="Storyboard Progress"
                  id="progress_development"
                  error={!!errors?.progress?.development}
                  helperText={errors?.progress?.development?.message}
                  variant="outlined"
                  type="number"
                  required
                  fullWidth
                  size="small"
                />
              )}
            />
          </div>
          <div className="sm:col-span-2">
            <Controller
              control={control}
              name="progress.production"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="IMI Progress"
                  placeholder="IMI Progress"
                  id="progress_production"
                  error={!!errors?.progress?.production}
                  helperText={errors?.progress?.production?.message}
                  variant="outlined"
                  type="number"
                  required
                  fullWidth
                  size="small"
                />
              )}
            />
          </div>
          <div className="sm:col-span-4">
            <Typography variant='body2' className='font-semibold'>Status:</Typography>
          </div>
          <div className="sm:col-span-2">
            <FormControl
              variant="outlined"
              fullWidth
              error={!!errors?.status?.development}
              required
            >
              <InputLabel id="status_development">Storyboard Status</InputLabel>
              <Controller
                control={control}
                name="status.development"
                defaultValue=""
                render={({ field: { onChange, value } }) => (
                  <Select
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                    }}
                    label="Storyboard Status"
                    placeholder="Storyboard Status"
                    id="status_development"
                    variant="outlined"
                    required
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="" disabled>Select a Status</MenuItem>
                    {Object.keys(SectionStatusSteps).map((key) => (
                      <MenuItem value={key} key={key} className="space-x-12">
                        <span>{SectionStatusSteps[key].label}</span>
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </div>
          <div className="sm:col-span-2">
            <FormControl
              variant="outlined"
              fullWidth
              error={!!errors?.status?.production}
              required
            >
              <InputLabel id="status_production">IMI Status</InputLabel>
              <Controller
                control={control}
                name="status.production"
                defaultValue=""
                render={({ field: { onChange, value } }) => (
                  <Select
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                    }}
                    label="Storyboard Status"
                    placeholder="Storyboard Status"
                    id="status_production"
                    variant="outlined"
                    required
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="" disabled>Select a Status</MenuItem>
                    {Object.keys(SectionStatusSteps).map((key) => (
                      <MenuItem value={key} key={key} className="space-x-12">
                        <span>{SectionStatusSteps[key].label}</span>
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </div>
        </div> 

        <Divider className="my-40" />

        <FormSectionHeader title="Section Navigation" description="Optional field for building out navigation flows." />
        <Controller
          control={control}
          name="navigation"
          render={({ field }) => <NavigationArray inputDir={inputDir} pageList={instance.pages} {...{ control, errors }} />}
        />
      </div>

      <Box
        className="flex items-center mt-40 py-14 pr-16 pl-4 sm:pr-48 sm:pl-36 border-t"
        sx={{ backgroundColor: 'background.default' }}
      >
        {routeParams.id !== 'new' && (
          authRoles.admin.includes(user.role) && (
            <DeleteModal onDelete={handleRemoveSection} resource="section" />
          )
        )}
        <Button
          component={Link}
          className="ml-auto"
          to="../"
        >
          Cancel
        </Button>
        <Button
          className="ml-8"
          variant="contained"
          color="secondary"
          disabled={_.isEmpty(dirtyFields) || !isValid}
          onClick={handleSubmit(onSubmit)}
        >
          Save
        </Button>
      </Box>
    </div>
  );
};

export default SectionForm;
